import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getErrorObjFormat } from "../../../helpers/errorObjFormat";
import moment from "moment";

const url = process.env.REACT_APP_API_BASE_URL + "/v1/orders";

// Handles both the list shape (flat fields) and the single-order shape (amounts object)
const formatOrder = (order) => {
  const total = order.amounts
    ? Number(order.amounts.post_discount_total ?? order.amounts.total)
    : Number(order.post_discount_total ?? order.total);
  const totalPaid = order.amounts
    ? Number(order.amounts.totalPaid)
    : Number(order.total_paid);
  const totalRefunded = order.amounts
    ? Number(order.amounts.totalRefunds ?? 0)
    : Number(order.total_refunded);

  const isPending = order.order_status === "PENDING";
  const hasBalance = totalPaid < total;

  return {
    ...order,
    total_owed: isPending ? total - totalPaid + totalRefunded : 0,
    charge_remaining_by:
      isPending && hasBalance
        ? moment.utc(order.check_in).subtract(7, "days").format("YYYY-MM-DD")
        : moment.utc(order.check_in).add(100, "years").format("YYYY-MM-DD"), // Add 100 so we can sort the dates that are pending ignoring the charge remaining by field
  };
};

const ordersInitialState = {
  ordersList: [],
  currentPageOrder: {},
  isLoading: false,
  error: null,

  /// Send Email
  isLoadingEmail: false,
  errorEmail: null,

  /// Send Cancellation Email
  isLoadingCancellationEmail: false,
  errorCancellationEmail: null,
};

function startLoading(state) {
  state.isLoading = true;
}
function loadingFailed(state, { payload }) {
  state.isLoading = false;
  state.error = payload;
}
function startLoadingEmail(state) {
  state.isLoadingEmail = true;
}
function loadingFailedEmail(state, { payload }) {
  state.isLoadingEmail = false;
  state.errorEmail = payload;
}
function startLoadingCancellationEmail(state) {
  state.isLoadingCancellationEmail = true;
}
function loadingFailedCancellationEmail(state, { payload }) {
  state.isLoadingCancellationEmail = false;
  state.errorCancellationEmail = payload;
}

const orders = createSlice({
  name: "orders",
  initialState: ordersInitialState,
  reducers: {
    getOrderStart: startLoading,
    getOrdersStart: startLoading,
    createOrderStart: startLoading,
    editOrderStart: startLoading,
    deleteOrderStart: startLoading,
    getOrderSuccess(state, { payload }) {
      state.currentPageOrder = payload;
      state.isLoading = false;
      state.error = null;
    },
    getOrdersSuccess(state, { payload }) {
      state.ordersList = payload;
      state.isLoading = false;
      state.error = null;
    },
    createOrderSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    editOrderSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    deleteOrderSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    getOrderFailure: loadingFailed,
    getOrdersFailure: loadingFailed,
    createOrderFailure: loadingFailed,
    editOrderFailure: loadingFailed,
    deleteOrderFailure: loadingFailed,

    // Email
    sendEmailStart: startLoadingEmail,
    sendEmailSuccess(state) {
      state.isLoadingEmail = false;
      state.errorEmail = null;
    },
    sendEmailFailure: loadingFailedEmail,

    // Cancellation Email
    sendCancellationEmailStart: startLoadingCancellationEmail,
    sendCancellationEmailSuccess(state) {
      state.isLoadingCancellationEmail = false;
      state.errorCancellationEmail = null;
    },
    sendCancellationEmailFailure: loadingFailedCancellationEmail,
  },
});

export const {
  getOrderStart,
  getOrdersStart,
  createOrderStart,
  editOrderStart,
  deleteOrderStart,
  getOrderSuccess,
  getOrdersSuccess,
  createOrderSuccess,
  editOrderSuccess,
  deleteOrderSuccess,
  getOrderFailure,
  getOrdersFailure,
  createOrderFailure,
  editOrderFailure,
  deleteOrderFailure,

  // Email
  sendEmailStart,
  sendEmailSuccess,
  sendEmailFailure,

  // Cancellation Email
  sendCancellationEmailStart,
  sendCancellationEmailSuccess,
  sendCancellationEmailFailure,
} = orders.actions;

export default orders.reducer;

export const fetchOrders = () => async (dispatch, getState) => {
  try {
    dispatch(getOrdersStart());
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });

    const formattedResponse = res.data.response.map(formatOrder);

    dispatch(getOrdersSuccess(formattedResponse));
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(getOrdersFailure(errorObj));
  }
};

export const fetchOrder = (id) => async (dispatch, getState) => {
  try {
    dispatch(getOrderStart());
    const res = await axios.get(`${url}/${id}`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(getOrderSuccess(formatOrder(res.data.response)));
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(getOrderFailure(errorObj));
  }
};

export const createOrder = (orderObj) => async (dispatch, getState) => {
  try {
    dispatch(createOrderStart());
    const res = await axios.post(url, orderObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(createOrderSuccess());
    return { error: false, id: res.data.response.insertId };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(createOrderFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const editOrder = (orderId, orderObj) => async (dispatch, getState) => {
  try {
    dispatch(editOrderStart());
    const res = await axios.put(`${url}/${orderId}`, orderObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(editOrderSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(editOrderFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

// Dry run of editOrder: the API applies the changes in a rolled-back
// transaction and returns the amounts the order would end up with.
export const previewOrderChanges =
  (orderId, orderObj) => async (dispatch, getState) => {
    try {
      const res = await axios.post(`${url}/${orderId}/preview`, orderObj, {
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      return { error: false, amounts: res.data.response.amounts };
    } catch (err) {
      return { error: true, ...getErrorObjFormat(err) };
    }
  };

export const deleteOrder = (orderId) => async (dispatch, getState) => {
  try {
    dispatch(deleteOrderStart());
    const res = await axios.delete(url, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
      data: { id: orderId },
    });
    dispatch(deleteOrderSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(deleteOrderFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

// Send Email
export const sendEmail = (orderId, emailObj) => async (dispatch, getState) => {
  try {
    dispatch(sendEmailStart());
    const res = await axios.post(`${url}/${orderId}/email`, emailObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(sendEmailSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);

    dispatch(sendEmailFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

// Send Cancellation Email
export const sendCancellationEmail =
  (orderId) => async (dispatch, getState) => {
    try {
      dispatch(sendCancellationEmailStart());
      const res = await axios.post(
        `${url}/${orderId}/cancelation-email`,
        {},
        {
          headers: { Authorization: `Bearer ${getState().auth.token}` },
        }
      );
      dispatch(sendCancellationEmailSuccess());
      return { error: false, id: res.data.response.id };
    } catch (err) {
      let errorObj = getErrorObjFormat(err);

      dispatch(sendCancellationEmailFailure(errorObj));
      return { error: true, ...errorObj };
    }
  };
